import React from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import type { KpiDrilldownSpec, KpiHeatmapCustomerApiData } from './kpiDashboardApi';
import KpiChartPanel from './charts/KpiChartPanel';
import {
  KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL,
  KPI_DASHBOARD_TABLE_TH_STICKY_TOP,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';

export type HeatmapVisualVariant = 'slate' | 'incident';

function heatCellStyleSlate(value: number, max: number): React.CSSProperties {
  if (value <= 0 || max <= 0) {
    return { backgroundColor: '#f1f5f9' };
  }
  const t = Math.min(1, value / max);
  const r = Math.round(241 + (67 - 241) * t);
  const g = Math.round(245 + (56 - 245) * t);
  const b = Math.round(249 + (202 - 249) * t);
  return { backgroundColor: `rgb(${r},${g},${b})` };
}

/** Blue heat scale — aligned with Bandwidth dashboard heatmap. */
export function heatCellStyleIncidentBlue(value: number, max: number): React.CSSProperties {
  if (value <= 0 || max <= 0) {
    return { backgroundColor: '#f8fafc' };
  }
  const t = Math.min(1, value / max);
  const alpha = 0.07 + t * 0.88;
  return { backgroundColor: `rgba(47, 85, 151, ${alpha})` };
}

export interface KpiCustomerHeatmapTableProps {
  title: string;
  variant?: HeatmapVisualVariant;
  heatmap: KpiHeatmapCustomerApiData['heatmap'];
  sortedRows: KpiHeatmapCustomerApiData['heatmap']['rows'];
  onToggleCustomerSort: () => void;
  maxCount: number;
  onCellDrill: (spec: KpiDrilldownSpec) => void;
  emptyMessage?: string;
}

const KpiCustomerHeatmapTable: React.FC<KpiCustomerHeatmapTableProps> = ({
  title,
  variant = 'slate',
  heatmap,
  sortedRows,
  onToggleCustomerSort,
  maxCount,
  onCellDrill,
  emptyMessage = 'No open Incident or Request backlog in this period for the selected filters.',
}) => {
  const maxH = maxCount > 0 ? maxCount : 1;
  const cellStyle = variant === 'incident' ? heatCellStyleIncidentBlue : heatCellStyleSlate;
  const stickyNameClass = `${KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL} min-w-[12rem] max-w-[16rem]`;
  const sortBtnClass =
    'inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left font-semibold text-slate-800 hover:bg-sky-200/50';

  return (
    <KpiChartPanel
      title={title}
      flexFill
      className="min-h-0"
      bodyClassName="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0"
    >
      <div className="max-h-[min(28rem,70vh)] min-h-0 overflow-auto">
        <table className="w-max min-w-full border-collapse text-left text-xs">
          <thead>
            <tr>
              <th className={stickyNameClass} scope="col">
                <button type="button" onClick={onToggleCustomerSort} className={sortBtnClass}>
                  Customer
                  <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                </button>
              </th>
              {heatmap.weekLabels.map((wl, i) => (
                <th key={`${heatmap.weekStarts[i] ?? i}-${wl}`} className={KPI_DASHBOARD_TABLE_TH_STICKY_TOP} scope="col">
                  {wl}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, heatmap.weekLabels.length + 1)}
                  className="border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, ri) => (
                <tr key={row.customerId} className={`border-b border-slate-200 ${kpiDashboardTableRowClass(ri)}`}>
                  <th
                    scope="row"
                    className="sticky left-0 z-10 min-w-[12rem] max-w-[16rem] border border-slate-200 bg-inherit px-3 py-2 text-left font-medium text-slate-900 shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)]"
                  >
                    <span className="line-clamp-2">{row.customerName}</span>
                  </th>
                  {row.counts.map((c, i) => {
                    const weekStart = heatmap.weekStarts[i];
                    const clickable = c > 0 && weekStart;
                    return (
                      <td
                        key={`${row.customerId}-${weekStart ?? i}`}
                        className={`border border-slate-200 px-1 py-1 text-center tabular-nums text-slate-800 ${
                          clickable ? 'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-[#4472C4]/50' : ''
                        }`}
                        style={cellStyle(c, maxH)}
                        onClick={
                          clickable
                            ? () =>
                                onCellDrill({
                                  kind: 'heatmap_cell',
                                  customerId: row.customerId,
                                  weekStart: weekStart!,
                                })
                            : undefined
                        }
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onCellDrill({
                                    kind: 'heatmap_cell',
                                    customerId: row.customerId,
                                    weekStart: weekStart!,
                                  });
                                }
                              }
                            : undefined
                        }
                        role={clickable ? 'button' : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        aria-label={
                          clickable
                            ? `Open tickets for ${row.customerName}, ${heatmap.weekLabels[i] ?? 'week'}, count ${c}`
                            : undefined
                        }
                      >
                        <span className="inline-block min-w-[1.5rem]">{c > 0 ? c : ''}</span>
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default KpiCustomerHeatmapTable;
